import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { type Product } from './pages/site/shopData';
import { useCart } from '../contexts/CartContext';
import { usePrice } from '../contexts/PriceContext';
export function ShopProductCard({
  product,
  addItem,
  isBestseller,
  reviewStats
}: {
  product: Product;
  addItem: ReturnType<typeof useCart>['addItem'];
  isBestseller: boolean;
  reviewStats?: {
    avg: number;
    count: number;
  };
}) {
  const {
    cents
  } = usePrice(product.lookupKey, product.price);
  const galleryImages = product.backImage && product.backImage !== product.image ? [product.image, product.backImage] : [product.image];
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const activeImage = galleryImages[activeImageIndex];
  const hasMultipleImages = galleryImages.length > 1;
  const handleAdd = () => {
    if (cents === null || !product.lookupKey) return;
    addItem({
      productId: product.id,
      name: product.name,
      unitPrice: cents,
      type: 'supplement',
      lookupKey: product.lookupKey
    });
  };
  const changeImage = (direction: 1 | -1) => {
    setActiveImageIndex(current => (current + direction + galleryImages.length) % galleryImages.length);
  };
  return <div className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white transition-[background-color,box-shadow] duration-200 ease-out hover:bg-[#E5E9F1] hover:shadow-lg" data-id="element-3454" data-mp="1c9uhcyv85edf">
      <div className="relative aspect-square shrink-0 bg-transparent p-6" data-id="element-3455" data-mp="138wggpg31mz2">
        {isBestseller && <span className="absolute top-3 left-3 z-20 px-2.5 py-1 bg-[#1F73B7] text-white text-[10px] font-bold tracking-wide rounded-sm" data-id="element-3456" data-mp="yb3a9wes4sys">
            Best Seller
          </span>}
        <Link to={`/shop/${product.id}`} className="absolute inset-6 block" aria-label={`View ${product.name}`} data-id="element-3457" data-mp="2snt5chgvw87r">
          <AnimatePresence mode="wait" initial={false} data-id="element-3458" data-mp="3bro3vo4agvli">
            <motion.img key={activeImage} src={activeImage} alt={product.name} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.18
          }} className="absolute inset-0 h-full w-full object-contain" data-id="element-3459" data-mp="3gjxr2we0rhk3" />
          </AnimatePresence>
        </Link>
        {hasMultipleImages && <div className="absolute inset-x-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-between" data-id="element-3460" data-mp="3b1sb5kd2j25">
            <button type="button" onClick={() => changeImage(-1)} className="site-nav-icon-button inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#D8D8D0] bg-white/95 shadow-sm transition-colors" aria-label={`Show previous image of ${product.name}`} data-id="element-3461" data-mp="3ll500yrwemsc">
              <ChevronLeft className="h-4 w-4" strokeWidth={1.75} data-id="element-3462" data-mp="27i4l6nt511gu" />
            </button>
            <button type="button" onClick={() => changeImage(1)} className="site-nav-icon-button inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#D8D8D0] bg-white/95 shadow-sm transition-colors" aria-label={`Show next image of ${product.name}`} data-id="element-3463" data-mp="22kw1gisccpdh">
              <ChevronRight className="h-4 w-4" strokeWidth={1.75} data-id="element-3464" data-mp="1nvgbv64jqpzd" />
            </button>
          </div>}
      </div>

      <div className="flex flex-1 flex-col p-5" data-id="element-3465" data-mp="1xkzuqbs9eju8">
        <div className="flex items-center gap-0.5 mb-3" data-id="element-3466" data-mp="1xlrr3otokllk">
          {[1, 2, 3, 4, 5].map(star => <Star key={star} className={`w-3.5 h-3.5 ${star <= Math.round(reviewStats?.avg || 0) ? 'fill-[#1F73B7] text-[#1F73B7]' : 'text-[#D8D8D0]'}`} data-id="element-3467" data-mp="1sthxnytcx3ej" />)}
          <span className="text-xs text-[#9A9A90] ml-1.5" data-id="element-3468" data-mp="1dt4acwjb36gf">
            {reviewStats?.count || 0} Reviews
          </span>
        </div>

        <Link to={`/shop/${product.id}`} className="hover:underline decoration-[#1F1F1F] underline-offset-2" data-id="element-3469" data-mp="1irpakvyk5q4v">
          <h3 className="text-lg font-bold text-[#1F1F1F] mb-2 leading-tight" data-id="element-3470" data-mp="1sgz32cxbdati">
            {product.name}
          </h3>
        </Link>
        <p className="text-sm text-[#5A5A5A] mb-5 line-clamp-2 flex-1" data-id="element-3471" data-mp="1whstbcy8b1pk">
          {product.shortDescription}
        </p>

        <div className="flex items-center justify-between mt-auto pt-4 border-t border-[#E5E5E5]" data-id="element-3472" data-mp="21g2mji0e7s8j">
          <p className="text-lg font-bold text-[#1F1F1F]" data-id="element-3473" data-mp="26hryr0zcqby7">
            {cents !== null ? '$' + (cents / 100).toFixed(2) : '—'}
          </p>
          <button type="button" onClick={handleAdd} disabled={cents === null || !product.lookupKey} className="px-5 py-2.5 bg-[#1F1F1F] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#3B3B3B] transition-colors disabled:opacity-40 rounded" data-id="element-3474" data-mp="c38y73ydeiit">
            Add To Cart
          </button>
        </div>
      </div>
    </div>;
}